<template>
  <q-page class="ascenso-page">
    <div class="ascenso-container">
      <!-- Loading state -->
      <div v-if="loading" class="ascenso-loading">
        <q-skeleton type="rect" width="100%" height="40px" dark class="q-mb-md" />
        <q-skeleton type="rect" width="100%" height="600px" dark />
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="ascenso-empty">
        <q-icon name="error_outline" size="48px" :style="{ color: 'var(--oa-danger)' }" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          :style="{ background: 'var(--oa-gold-full)', color: 'var(--color-marble-cream)', fontWeight: '600', borderRadius: '8px' }"
          @click="loadSkillTree"
        />
      </div>

      <!-- Main content -->
      <template v-else>
        <!-- Header -->
        <div class="ascenso-header">
          <div>
            <h1 class="ascenso-title">El Ascenso</h1>
            <p class="ascenso-subtitle">
              {{ completedCount }}/{{ totalCount }} nodos &middot; {{ progressPct }}%
            </p>
          </div>
          <div class="ascenso-header-right">
            <div class="ascenso-progress-bar-container">
              <div class="ascenso-progress-bar" :style="{ width: progressPct + '%' }" />
            </div>
            <span class="ascenso-xp">{{ userXp.toLocaleString() }}</span>
          </div>
        </div>

        <!-- Map container -->
        <div class="ascenso-map-wrapper">
          <!-- Region jump sidebar -->
          <div class="region-sidebar">
            <button
              v-for="r in REGIONS"
              :key="r.key"
              class="region-btn"
              :disabled="getRegionState(r.key) === 'locked'"
              :style="{ opacity: getRegionState(r.key) === 'locked' ? 0.3 : 1 }"
              @click="scrollToRegion(r.key)"
            >
              <span
                class="region-dot"
                :style="{ background: getRegionState(r.key) === 'locked' ? 'rgba(61, 55, 50, 0.12)' : r.color }"
              />
              <span
                class="region-label"
                :style="{ color: getRegionState(r.key) === 'locked' ? 'rgba(61, 55, 50, 0.12)' : r.color }"
              >
                {{ r.label }}
              </span>
            </button>
          </div>

          <!-- Scrollable SVG area -->
          <div ref="scrollContainer" class="ascenso-scroll-area">
            <svg
              :viewBox="`0 -30 820 ${maxY + 30}`"
              class="ascenso-svg"
            >
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="glow-strong">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="glow-boss">
                  <feGaussianBlur stdDeviation="10" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <!-- Region backgrounds -->
              <g v-for="r in REGIONS" :key="'region-' + r.key">
                <rect
                  :x="r.xStart"
                  :y="r.yStart"
                  :width="r.xEnd - r.xStart"
                  :height="r.yEnd - r.yStart"
                  rx="16"
                  ry="16"
                  :fill="getRegionState(r.key) === 'locked' ? 'rgba(15,14,13,0.4)' : r.color + '08'"
                  :stroke="r.color"
                  :stroke-width="getRegionState(r.key) === 'locked' ? 0.3 : 0.8"
                  :stroke-opacity="getRegionState(r.key) === 'locked' ? 0.06 : 0.15"
                  :stroke-dasharray="getRegionState(r.key) === 'locked' ? '4 6' : '0'"
                />

                <!-- Glow for active regions -->
                <template v-if="getRegionState(r.key) !== 'locked'">
                  <ellipse
                    :cx="r.xStart + (r.xEnd - r.xStart) / 2"
                    :cy="r.yStart + (r.yEnd - r.yStart) * 0.3"
                    :rx="(r.xEnd - r.xStart) / 2"
                    :ry="(r.yEnd - r.yStart) / 3"
                    :fill="r.color"
                    opacity="0.025"
                  />
                  <rect
                    :x="r.xStart + 2"
                    :y="r.yStart + 2"
                    :width="r.xEnd - r.xStart - 4"
                    :height="r.yEnd - r.yStart - 4"
                    rx="14"
                    ry="14"
                    fill="none"
                    :stroke="r.color"
                    stroke-width="0.3"
                    stroke-opacity="0.06"
                  />
                </template>

                <!-- Region labels -->
                <text
                  :x="r.xStart + 16"
                  :y="r.yStart + 24"
                  font-size="14"
                  font-weight="700"
                  letter-spacing="0.5"
                  :fill="r.color"
                  :opacity="getRegionState(r.key) === 'locked' ? 0.12 : getRegionState(r.key) === 'completed' ? 0.7 : 0.5"
                >
                  {{ r.label }}
                </text>
                <text
                  :x="r.xStart + 16"
                  :y="r.yStart + 38"
                  font-size="9.5"
                  font-style="italic"
                  :fill="r.color"
                  :opacity="getRegionState(r.key) === 'locked' ? 0.06 : 0.2"
                >
                  {{ r.greek }}
                </text>

                <!-- Fog of war for locked regions -->
                <rect
                  v-if="getRegionState(r.key) === 'locked'"
                  :x="r.xStart"
                  :y="r.yStart"
                  :width="r.xEnd - r.xStart"
                  :height="r.yEnd - r.yStart"
                  rx="16"
                  ry="16"
                  fill="var(--color-marble-cream)"
                  opacity="0.6"
                />
              </g>

              <!-- Constellation dots -->
              <circle
                v-for="(dot, i) in constellationDots"
                :key="'dot-' + i"
                :cx="dot[0]"
                :cy="dot[1]"
                :r="dot[2]"
                fill="var(--color-terracotta)"
                opacity="0.05"
              />

              <!-- Connection paths -->
              <path
                v-for="(conn, i) in layoutConnections"
                :key="'edge-' + i"
                :d="getConnectionPath(conn)"
                fill="none"
                :stroke="getConnectionColor(conn)"
                :stroke-width="conn.to.nodeType === 'bonus' ? 1.5 : 2"
                :stroke-opacity="isConnectionActive(conn) ? 0.7 : 0.3"
                :stroke-dasharray="getConnectionDash(conn)"
              />

              <!-- Nodes -->
              <g
                v-for="node in layoutNodes"
                :key="node.id"
                :class="{ 'node-clickable': node.status !== 'locked' }"
                @click="onNodeClick(node)"
              >
                <!-- Boss outer corona rings -->
                <template v-if="node.nodeType === 'boss' && node.status !== 'locked'">
                  <circle :cx="node.positionX" :cy="node.positionY" :r="getNodeRadius(node) + 14" fill="none" stroke="var(--color-terracotta)" stroke-width="0.5" opacity="0.2" />
                  <circle :cx="node.positionX" :cy="node.positionY" :r="getNodeRadius(node) + 24" fill="none" stroke="var(--color-terracotta)" stroke-width="0.3" opacity="0.1" />
                  <circle :cx="node.positionX" :cy="node.positionY" :r="getNodeRadius(node) + 34" fill="none" stroke="var(--color-terracotta)" stroke-width="0.2" opacity="0.05" />
                </template>

                <!-- Pulse ring for available/in_progress -->
                <template v-if="node.status === 'available' || node.status === 'in_progress'">
                  <circle
                    :cx="node.positionX"
                    :cy="node.positionY"
                    :r="getNodeRadius(node) + 6"
                    fill="none"
                    stroke="var(--color-terracotta)"
                    stroke-width="1.5"
                    opacity="0.3"
                  >
                    <animate attributeName="r" :from="getNodeRadius(node) + 3" :to="getNodeRadius(node) + 16" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.4" to="0" dur="2s" repeatCount="indefinite" />
                  </circle>
                </template>

                <!-- Glow for completed/mastered -->
                <circle
                  v-if="isNodeCompleted(node)"
                  :cx="node.positionX"
                  :cy="node.positionY"
                  :r="getNodeRadius(node) + 5"
                  fill="var(--color-terracotta)"
                  opacity="0.15"
                  :filter="node.status === 'mastered' ? 'url(#glow-strong)' : 'url(#glow)'"
                />

                <!-- Entry node extra glow ring -->
                <circle
                  v-if="isEntryNode(node)"
                  :cx="node.positionX"
                  :cy="node.positionY"
                  :r="getNodeRadius(node) + 8"
                  fill="none"
                  stroke="var(--color-terracotta)"
                  stroke-width="1"
                  opacity="0.12"
                />

                <!-- Main shape: diamond or circle -->
                <polygon
                  v-if="getNodeShape(node) === 'diamond'"
                  :points="getDiamondPoints(node)"
                  :fill="getNodeFill(node)"
                  :opacity="node.status === 'locked' ? 0.5 : 1"
                  :stroke="getNodeStroke(node)"
                  :stroke-width="getNodeStrokeWidth(node)"
                  stroke-linejoin="round"
                  :filter="isNodeCompleted(node) ? 'url(#glow)' : undefined"
                />
                <circle
                  v-else
                  :cx="node.positionX"
                  :cy="node.positionY"
                  :r="getNodeRadius(node)"
                  :fill="getNodeFill(node)"
                  :opacity="node.status === 'locked' ? 0.5 : 1"
                  :stroke="getNodeStroke(node)"
                  :stroke-width="getNodeStrokeWidth(node)"
                  :filter="isNodeCompleted(node) ? (node.nodeType === 'boss' ? 'url(#glow-boss)' : 'url(#glow)') : undefined"
                />

                <!-- Status icon (SVG text symbols) -->
                <text
                  :x="node.positionX"
                  :y="node.positionY + getIconSize(node) * 0.35"
                  text-anchor="middle"
                  :font-size="getIconSize(node)"
                  :fill="getIconColor(node)"
                  :opacity="node.status === 'locked' ? 0.5 : 1"
                >
                  {{ getNodeIcon(node) }}
                </text>

                <!-- Gateway decorative columns -->
                <template v-if="node.nodeType === 'gateway' && node.status !== 'locked'">
                  <g :transform="`translate(${node.positionX - getNodeRadius(node) - 8}, ${node.positionY - 12})`" opacity="0.2">
                    <rect x="0" y="0" width="4" height="24" rx="1" fill="var(--color-terracotta)" />
                    <rect x="-1" y="-2" width="6" height="3" rx="1" fill="var(--color-terracotta)" />
                    <rect x="-1" y="23" width="6" height="3" rx="1" fill="var(--color-terracotta)" />
                  </g>
                  <g :transform="`translate(${node.positionX + getNodeRadius(node) + 4}, ${node.positionY - 12})`" opacity="0.2">
                    <rect x="0" y="0" width="4" height="24" rx="1" fill="var(--color-terracotta)" />
                    <rect x="-1" y="-2" width="6" height="3" rx="1" fill="var(--color-terracotta)" />
                    <rect x="-1" y="23" width="6" height="3" rx="1" fill="var(--color-terracotta)" />
                  </g>
                </template>

                <!-- Node label -->
                <text
                  :x="node.positionX"
                  :y="getNodeLabelY(node)"
                  text-anchor="middle"
                  :font-size="getNodeLabelSize(node)"
                  :font-weight="isNodeCompleted(node) || isEntryNode(node) || node.nodeType === 'boss' ? '600' : '500'"
                  :font-style="isEntryNode(node) ? 'italic' : 'normal'"
                  :fill="getNodeLabelFill(node)"
                >
                  {{ truncateLabel(node) }}
                </text>

                <!-- Hero epithet for boss nodes -->
                <text
                  v-if="node.nodeType === 'boss' && node.unlockContent?.heroEpithet"
                  :x="node.positionX"
                  :y="getNodeLabelY(node) + 13"
                  text-anchor="middle"
                  font-size="7.5"
                  font-style="italic"
                  :fill="isNodeCompleted(node) ? 'var(--color-aged-gold)' : 'rgba(184, 155, 94, 0.55)'"
                  :opacity="node.status === 'locked' ? 0.3 : 0.7"
                >
                  {{ node.unlockContent.heroEpithet }}
                </text>

                <!-- XP badge -->
                <g
                  v-if="node.status !== 'locked'"
                  :transform="`translate(${node.positionX + getNodeRadius(node) - 4}, ${node.positionY - getNodeRadius(node) - 2})`"
                >
                  <rect
                    x="-12"
                    y="-7"
                    width="24"
                    height="14"
                    rx="7"
                    :fill="node.nodeType === 'bonus' ? '#8b7a4a' : 'var(--color-terracotta)'"
                    opacity="0.9"
                  />
                  <text
                    x="0"
                    y="3.5"
                    text-anchor="middle"
                    font-size="7"
                    font-weight="bold"
                    fill="var(--color-marble-cream)"
                  >
                    +{{ node.xpReward }}
                  </text>
                </g>
              </g>
            </svg>
          </div>

          <!-- Scroll fade -->
          <div class="ascenso-scroll-fade" />

          <!-- Legend -->
          <div class="ascenso-legend">
            <span
              v-for="item in legendItems"
              :key="item.label"
              class="legend-item"
            >
              <span
                class="legend-dot"
                :style="{
                  background: item.bg,
                  border: '1.5px solid ' + item.border,
                  transform: item.rotate ? 'rotate(45deg)' : undefined,
                  borderRadius: item.rotate ? '2px' : '50%',
                }"
              />
              {{ item.label }}
            </span>
          </div>
        </div>
      </template>

      <!-- Node detail dialog -->
      <q-dialog v-model="showNodeDialog" position="right" full-height>
        <q-card class="node-detail-card" v-if="selectedNode">
          <!-- Dialog header -->
          <q-card-section class="node-detail-header">
            <div class="node-detail-header-row">
              <div class="node-detail-header-info">
                <q-icon :name="getNodeTypeIcon(selectedNode)" size="24px" :style="{ color: 'var(--oa-gold-full)' }" />
                <div>
                  <h3 class="node-detail-title">{{ selectedNode.label }}</h3>
                  <p class="node-detail-type">{{ selectedNode.nodeType }}</p>
                </div>
              </div>
              <q-btn flat round dense icon="close" :style="{ color: 'var(--oa-text-muted)' }" @click="showNodeDialog = false" />
            </div>
          </q-card-section>

          <!-- Dialog body -->
          <q-card-section class="node-detail-body">
            <p v-if="selectedNode.description" class="node-detail-desc">
              {{ selectedNode.description }}
            </p>

            <div class="node-detail-badges">
              <span class="badge-status" :class="'badge-' + selectedNode.status">
                {{ statusLabels[selectedNode.status] || 'Bloqueado' }}
              </span>
              <span class="badge-xp">+{{ selectedNode.xpReward }} AURA</span>
            </div>

            <!-- Progress bar for in_progress -->
            <div v-if="selectedNode.status === 'in_progress'" class="node-progress-section">
              <div class="node-progress-labels">
                <span>Progreso</span>
                <span>{{ selectedNode.progressPct }}%</span>
              </div>
              <q-linear-progress
                :value="selectedNode.progressPct / 100"
                color="amber"
                track-color="grey-9"
                rounded
                size="6px"
              />
            </div>

            <!-- Unlock condition -->
            <div v-if="selectedNode.unlockCondition" class="node-detail-section">
              <p class="node-section-label">Requisito</p>
              <p class="node-section-text">{{ getUnlockConditionText(selectedNode) }}</p>
            </div>

            <!-- Hero info for boss nodes -->
            <div v-if="selectedNode.unlockContent?.hero" class="node-hero-section">
              <p class="node-hero-label">Guardian</p>
              <p class="node-hero-name">{{ selectedNode.unlockContent.hero }}</p>
              <p v-if="selectedNode.unlockContent.heroEpithet" class="node-hero-epithet">
                {{ selectedNode.unlockContent.heroEpithet }}
              </p>
            </div>

            <!-- Phrase -->
            <div v-if="selectedNode.unlockContent?.phrase" class="node-phrase-section">
              <p class="node-phrase-text">
                &ldquo;{{ selectedNode.unlockContent.phrase }}&rdquo;
              </p>
            </div>

            <!-- Region info -->
            <p v-if="selectedNode.region" class="node-region-info">
              Region: {{ selectedNode.region.charAt(0).toUpperCase() + selectedNode.region.slice(1) }}
            </p>

            <!-- Tip -->
            <p v-if="selectedNode.unlockContent?.tip" class="node-tip-text">
              {{ selectedNode.unlockContent.tip }}
            </p>

            <!-- Complete button -->
            <q-btn
              v-if="selectedNode.status === 'available'"
              no-caps
              unelevated
              class="node-complete-btn"
              :loading="completing"
              label="Completar nodo"
              icon="check_circle"
              @click="completeNode(selectedNode)"
            />
          </q-card-section>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { api } from 'boot/axios';
import { useQuasar } from 'quasar';

// ── Types ──

interface SkillNode {
  id: string;
  key: string;
  label: string;
  description: string | null;
  nodeType: string;
  positionX: number;
  positionY: number;
  xpReward: number;
  orderIndex: number;
  unlockCondition: {
    type: 'node_complete' | 'xp_threshold' | 'cert_level' | 'manual';
    nodeId?: string;
    value?: number;
    level?: string;
  } | null;
  region: string | null;
  unlockContent: {
    phrase?: string;
    tip?: string;
    bonusUrl?: string;
    hero?: string;
    heroEpithet?: string;
  } | null;
  status: 'locked' | 'available' | 'in_progress' | 'completed' | 'mastered';
  progressPct: number;
}

interface MapConnection {
  from: SkillNode;
  to: SkillNode;
}

interface RegionDef {
  key: string;
  label: string;
  greek: string;
  color: string;
  yStart: number;
  yEnd: number;
  xStart: number;
  xEnd: number;
}

// ── Constants ──

const REGIONS: RegionDef[] = [
  { key: 'agora',     label: 'Agora',     greek: '\u1F08\u03B3\u03BF\u03C1\u03AC',         color: '#4ade80', yStart: -10, yEnd: 1650, xStart: 30, xEnd: 790 },
  { key: 'academia',  label: 'Academia',  greek: '\u1F08\u03BA\u03B1\u03B4\u03B7\u03BC\u03AF\u03B1',      color: '#60a5fa', yStart: 1700, yEnd: 2780, xStart: 20, xEnd: 370 },
  { key: 'santuario', label: 'Santuario', greek: '\u1F39\u03B5\u03C1\u03CC\u03BD',         color: '#c084fc', yStart: 1700, yEnd: 2660, xStart: 350, xEnd: 510 },
  { key: 'estadio',   label: 'Estadio',   greek: '\u03A3\u03C4\u03AC\u03B4\u03B9\u03BF\u03BD',       color: '#f87171', yStart: 1700, yEnd: 2480, xStart: 520, xEnd: 780 },
  { key: 'liceo',     label: 'Liceo',     greek: '\u039B\u03CD\u03BA\u03B5\u03B9\u03BF\u03BD',       color: '#a78bfa', yStart: 2830, yEnd: 3700, xStart: 20, xEnd: 370 },
  { key: 'gimnasio',  label: 'Gimnasio',  greek: '\u0393\u03C5\u03BC\u03BD\u03AC\u03C3\u03B9\u03BF\u03BD',     color: '#fb923c', yStart: 2830, yEnd: 3960, xStart: 430, xEnd: 800 },
  { key: 'pritaneo',  label: 'Pritaneo',  greek: '\u03A0\u03C1\u03C5\u03C4\u03B1\u03BD\u03B5\u1FD6\u03BF\u03BD',    color: '#fbbf24', yStart: 4010, yEnd: 4880, xStart: 20, xEnd: 310 },
  { key: 'areopago',  label: 'Areopago',  greek: '\u1F0C\u03C1\u03B5\u03B9\u03BF\u03C2 \u03A0\u03AC\u03B3\u03BF\u03C2',  color: '#2dd4bf', yStart: 4010, yEnd: 4730, xStart: 320, xEnd: 520 },
  { key: 'palestra',  label: 'Palestra',  greek: '\u03A0\u03B1\u03BB\u03B1\u03AF\u03C3\u03C4\u03C1\u03B1',     color: '#f472b6', yStart: 4010, yEnd: 4930, xStart: 530, xEnd: 790 },
  { key: 'olimpo',    label: 'El Olimpo', greek: '\u1F4C\u03BB\u03C5\u03BC\u03C0\u03BF\u03C2',       color: 'var(--color-terracotta)', yStart: 5000, yEnd: 5220, xStart: 250, xEnd: 560 },
];

const SERPENTINE: Record<string, [number, number]> = {
  // CC Region "Agora" (Y 50-1570)
  'inicio':            [400, 50],
  'ud1-identidad':     [160, 180],
  'ud1-gate':          [300, 280],
  'ud2-estimulo':      [640, 390],
  'ud2-gate':          [480, 480],
  'ud3-progresion':    [160, 590],
  'ud3-gate':          [300, 680],
  'ud4-herramientas':  [640, 790],
  'ud4-gate':          [480, 880],
  'ud5-ciencia':       [160, 990],
  'ud5-gate':          [300, 1080],
  'ud6-persona':       [640, 1190],
  'ud6-gate':          [480, 1280],
  'ud7-integracion':   [400, 1410],
  'clase-especial':    [400, 1570],
  'bonus-biomecanica': [760, 410],
  'bonus-nutricion':   [760, 810],

  // Gateways Level 1
  'sc-gateway':       [140, 1760],
  'kairos-gateway':   [400, 1780],
  'gj-gateway':       [660, 1760],

  // SC Region "Academia"
  'sc-ud1-tecnicas':      [140, 1920],
  'sc-gate-1':            [240, 2010],
  'sc-ud2-evaluacion':    [100, 2100],
  'sc-gate-2':            [200, 2190],
  'sc-ud3-periodizacion': [140, 2280],
  'sc-gate-3':            [240, 2370],
  'sc-ud4-competencia':   [100, 2460],
  'sc-gate-4':            [200, 2550],
  'sc-boss':              [160, 2700],
  'sc-bonus':             [30, 2200],

  // Kairos Region "Santuario"
  'kairos-ud1-adaptacion':     [420, 1940],
  'kairos-gate-1':             [380, 2040],
  'kairos-ud2-inclusion':      [440, 2140],
  'kairos-gate-2':             [400, 2240],
  'kairos-ud3-especializacion':[430, 2340],
  'kairos-gate-3':             [390, 2440],
  'kairos-boss':               [410, 2580],

  // GJ Region "Estadio"
  'gj-ud1-reglamento':  [660, 1920],
  'gj-gate-1':          [600, 2020],
  'gj-ud2-jueceo':      [700, 2120],
  'gj-gate-2':          [640, 2220],
  'gj-boss':            [660, 2400],

  // Gateways Level 2
  'c1-gateway':  [160, 2870],
  'ot-gateway':  [660, 2870],

  // C1 Region "Liceo"
  'c1-ud1-pedagogia':  [140, 3020],
  'c1-gate-1':         [240, 3110],
  'c1-ud2-diseno':     [100, 3200],
  'c1-gate-2':         [200, 3290],
  'c1-ud3-mentoria':   [140, 3380],
  'c1-gate-3':         [240, 3470],
  'c1-boss':           [160, 3600],
  'c1-bonus':          [30, 3250],

  // OT Region "Gimnasio"
  'ot_1-ud1-operaciones':   [660, 3020],
  'ot_1-gate-1':            [580, 3100],
  'ot_1-ud2-incorporacion': [700, 3180],
  'ot_1-gate-2':            [620, 3260],
  'ot_1-ud3-metricas':      [660, 3340],
  'ot_1-gate-3':            [580, 3420],
  'ot_1-ud4-calidad':       [700, 3500],
  'ot_1-gate-4':            [620, 3580],
  'ot_1-ud5-crecimiento':   [660, 3660],
  'ot_1-gate-5':            [580, 3740],
  'ot_1-boss':              [650, 3880],
  'ot_1-bonus':             [770, 3400],

  // Gateways Level 3
  'c2-gateway':    [140, 4050],
  'oa-gateway':    [410, 4050],
  'owner-gateway': [660, 4050],

  // C2 Region "Pritaneo"
  'c2-ud1-liderazgo':    [140, 4200],
  'c2-gate-1':           [220, 4290],
  'c2-ud2-gestion':      [100, 4380],
  'c2-gate-2':           [180, 4470],
  'c2-ud3-estrategia':   [140, 4560],
  'c2-gate-3':           [220, 4650],
  'c2-boss':             [160, 4800],

  // OA Region "Areopago"
  'oa-ud1-estandares':  [410, 4200],
  'oa-gate-1':          [380, 4300],
  'oa-ud2-auditoria':   [430, 4400],
  'oa-gate-2':          [400, 4500],
  'oa-boss':            [410, 4650],

  // Owner Region "Palestra"
  'owner-ud1-expansion':      [660, 4200],
  'owner-gate-1':             [600, 4300],
  'owner-ud2-administracion': [700, 4400],
  'owner-gate-2':             [640, 4500],
  'owner-ud3-ecosistema':     [660, 4600],
  'owner-gate-3':             [600, 4700],
  'owner-boss':               [660, 4850],

  // El Olimpo
  'olimpo-final':  [400, 5100],
};

const constellationDots = [
  [70, 100, 0.8], [740, 160, 0.6], [50, 380, 0.5], [780, 550, 0.7],
  [80, 750, 0.4], [720, 950, 0.6], [40, 1150, 0.5], [790, 1350, 0.8],
  [250, 40, 0.4], [550, 130, 0.3], [120, 480, 0.5], [690, 680, 0.4],
  [320, 1860, 0.3], [510, 2060, 0.5], [180, 2960, 0.3], [600, 3460, 0.4],
  [100, 4060, 0.5], [700, 4260, 0.4], [350, 4660, 0.6], [500, 5060, 0.5],
];

const legendItems = [
  { label: 'Bloqueado', bg: 'var(--color-warm-linen)', border: 'rgba(61, 55, 50, 0.12)', rotate: false },
  { label: 'Disponible', bg: 'var(--color-sandy-beige)', border: 'var(--color-aged-gold)', rotate: false },
  { label: 'En progreso', bg: '#2a2520', border: 'var(--color-terracotta)', rotate: false },
  { label: 'Completado', bg: 'var(--color-terracotta)', border: 'var(--color-clay)', rotate: false },
  { label: 'Gate', bg: 'var(--color-sandy-beige)', border: 'var(--color-aged-gold)', rotate: true },
];

const statusLabels: Record<string, string> = {
  completed: 'Completado',
  mastered: 'Dominado',
  in_progress: 'En Progreso',
  available: 'Disponible',
  locked: 'Bloqueado',
};

const TYPE_CONFIG: Record<string, { r: number; shape: 'circle' | 'diamond' }> = {
  temple:  { r: 28, shape: 'circle' },
  gate:    { r: 18, shape: 'diamond' },
  gateway: { r: 20, shape: 'diamond' },
  boss:    { r: 44, shape: 'circle' },
  bonus:   { r: 16, shape: 'circle' },
};

// ── State ──

const $q = useQuasar();
const loading = ref(true);
const error = ref<string | null>(null);
const nodes = ref<SkillNode[]>([]);
const userXp = ref(0);
const selectedNode = ref<SkillNode | null>(null);
const showNodeDialog = ref(false);
const completing = ref(false);
const scrollContainer = ref<HTMLDivElement | null>(null);

// ── Computed ──

const layoutNodes = computed(() =>
  nodes.value.map((node) => {
    const pos = SERPENTINE[node.key];
    return pos ? { ...node, positionX: pos[0], positionY: pos[1] } : node;
  }),
);

const layoutConnections = computed<MapConnection[]>(() => {
  const nodeByKey = new Map(layoutNodes.value.map((n) => [n.key, n]));
  const connections: MapConnection[] = [];

  for (const node of layoutNodes.value) {
    if (node.unlockCondition?.type === 'node_complete' && node.unlockCondition.nodeId) {
      const parent = nodeByKey.get(node.unlockCondition.nodeId);
      if (parent) connections.push({ from: parent, to: node });
    } else if (node.unlockCondition?.type === 'cert_level' && node.unlockCondition.level) {
      const certRegion = getCertRegion(node.unlockCondition.level);
      const bossNode = layoutNodes.value.find(
        (n) => n.nodeType === 'boss' && n.unlockCondition?.type === 'node_complete' && n.region === certRegion,
      );
      const fallbackBoss = layoutNodes.value.find(
        (n) => n.key === `${node.unlockCondition!.level}-boss` || n.key === 'clase-especial',
      );
      const parent = bossNode ?? fallbackBoss;
      if (parent) connections.push({ from: parent, to: node });
    } else if (node.unlockCondition?.type === 'xp_threshold') {
      const mainNodes = layoutNodes.value.filter(
        (n) => n.nodeType !== 'bonus' && n.region === node.region,
      );
      if (mainNodes.length > 0) {
        const nearest = mainNodes.reduce((best, n) =>
          Math.abs(n.positionY - node.positionY) < Math.abs(best.positionY - node.positionY) ? n : best,
        );
        connections.push({ from: nearest, to: node });
      }
    }
  }

  return connections;
});

const maxY = computed(() =>
  Math.max(...layoutNodes.value.map((n) => n.positionY), 0) + 180,
);

const completedCount = computed(() =>
  nodes.value.filter((n) => n.status === 'completed' || n.status === 'mastered').length,
);

const totalCount = computed(() => nodes.value.length);

const progressPct = computed(() =>
  totalCount.value > 0 ? Math.round((completedCount.value / totalCount.value) * 100) : 0,
);

// ── Methods ──

function getCertRegion(certLevel: string): string {
  const map: Record<string, string> = {
    cc: 'agora', sc: 'academia', c1: 'liceo', c2: 'pritaneo',
    ot_1: 'gimnasio', owner: 'palestra', oa: 'areopago',
    kairos: 'santuario', gj: 'estadio',
  };
  return map[certLevel] ?? '';
}

function getRegionState(regionKey: string): 'locked' | 'active' | 'completed' {
  const regionNodes = layoutNodes.value.filter((n) => n.region === regionKey);
  if (regionNodes.length === 0) return 'locked';
  const hasNonLocked = regionNodes.some((n) => n.status !== 'locked');
  const allCompleted = regionNodes.every(
    (n) => n.status === 'completed' || n.status === 'mastered',
  );
  return allCompleted ? 'completed' : hasNonLocked ? 'active' : 'locked';
}

function scrollToRegion(regionKey: string) {
  const region = REGIONS.find((r) => r.key === regionKey);
  if (!region || !scrollContainer.value) return;
  const svgHeight = maxY.value + 60;
  const scrollableHeight = scrollContainer.value.scrollHeight;
  const targetRatio = region.yStart / svgHeight;
  scrollContainer.value.scrollTo({
    top: targetRatio * scrollableHeight - 40,
    behavior: 'smooth',
  });
}

function isEntryNode(node: SkillNode): boolean {
  return !node.unlockCondition && node.orderIndex === 0;
}

function isNodeCompleted(node: SkillNode): boolean {
  return node.status === 'completed' || node.status === 'mastered';
}

function getNodeRadius(node: SkillNode): number {
  if (isEntryNode(node)) return 36;
  const config = TYPE_CONFIG[node.nodeType] ?? TYPE_CONFIG.temple;
  return config.r;
}

function getNodeShape(node: SkillNode): 'circle' | 'diamond' {
  if (isEntryNode(node)) return 'circle';
  const config = TYPE_CONFIG[node.nodeType] ?? TYPE_CONFIG.temple;
  return config.shape;
}

function getDiamondPoints(node: SkillNode): string {
  const r = getNodeRadius(node);
  const cx = node.positionX;
  const cy = node.positionY;
  return `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
}

function getNodeFill(node: SkillNode): string {
  const entry = isEntryNode(node);
  if (node.status === 'locked') return 'var(--color-warm-linen)';
  if (isNodeCompleted(node)) return node.nodeType === 'bonus' ? 'var(--color-aged-gold)' : 'var(--color-terracotta)';
  if (node.status === 'in_progress') return '#2a2520';
  if (node.status === 'available') return entry ? 'var(--color-terracotta)' : 'var(--color-sandy-beige)';
  return 'var(--color-warm-linen)';
}

function getNodeStroke(node: SkillNode): string {
  if (node.status === 'locked') return 'rgba(61, 55, 50, 0.12)';
  if (isNodeCompleted(node)) return 'var(--color-clay)';
  if (node.status === 'in_progress') return 'var(--color-terracotta)';
  if (node.status === 'available') return 'var(--color-aged-gold)';
  return 'rgba(61, 55, 50, 0.12)';
}

function getNodeStrokeWidth(node: SkillNode): number {
  if (isNodeCompleted(node)) return 2.5;
  if (node.nodeType === 'boss') return 3;
  if (isEntryNode(node)) return 3;
  if (node.status === 'in_progress') return 2;
  return 1.5;
}

function getIconSize(node: SkillNode): number {
  if (node.nodeType === 'boss') return 22;
  if (isEntryNode(node)) return 16;
  if (node.nodeType === 'gate' || node.nodeType === 'gateway') return 12;
  if (node.nodeType === 'bonus') return 10;
  return 14;
}

function getIconColor(node: SkillNode): string {
  if (node.status === 'locked') return 'rgba(61, 55, 50, 0.12)';
  if (isNodeCompleted(node)) return 'var(--color-marble-cream)';
  if (node.status === 'available') return isEntryNode(node) ? 'var(--color-marble-cream)' : 'var(--color-aged-gold)';
  if (node.status === 'in_progress') return 'var(--color-terracotta)';
  return 'rgba(61, 55, 50, 0.12)';
}

function getNodeIcon(node: SkillNode): string {
  if (node.status === 'locked') return '\u{1F512}';
  if (node.status === 'mastered') return '\u{1F451}';
  if (node.status === 'completed') return '\u2713';
  if (node.status === 'in_progress') return '\u2726';
  if (node.status === 'available') return '\u25B6';
  return '';
}

function getNodeLabelY(node: SkillNode): number {
  const r = getNodeRadius(node);
  if (node.nodeType === 'gate' || node.nodeType === 'gateway') return node.positionY + r + 12;
  if (node.nodeType === 'boss') return node.positionY + r + 22;
  return node.positionY + r + 16;
}

function getNodeLabelSize(node: SkillNode): string {
  if (node.nodeType === 'boss') return '12';
  if (isEntryNode(node)) return '11';
  if (node.nodeType === 'gate' || node.nodeType === 'gateway' || node.nodeType === 'bonus') return '8.5';
  return '10';
}

function getNodeLabelFill(node: SkillNode): string {
  if (node.status === 'locked') return 'rgba(61, 55, 50, 0.12)';
  if (isNodeCompleted(node)) return 'var(--color-terracotta)';
  if (node.status === 'in_progress') return 'var(--color-deep-charcoal)';
  if (isEntryNode(node)) return 'var(--color-clay)';
  return 'var(--color-charcoal-mist)';
}

function truncateLabel(node: SkillNode): string {
  const maxLen = node.nodeType === 'boss' ? 30 : node.nodeType === 'gate' || node.nodeType === 'gateway' ? 22 : 26;
  return node.label.length > maxLen ? node.label.slice(0, maxLen - 2) + '...' : node.label;
}

function getConnectionPath(conn: MapConnection): string {
  const midY = (conn.from.positionY + conn.to.positionY) / 2;
  return `M ${conn.from.positionX} ${conn.from.positionY} C ${conn.from.positionX} ${midY}, ${conn.to.positionX} ${midY}, ${conn.to.positionX} ${conn.to.positionY}`;
}

function getConnectionColor(conn: MapConnection): string {
  const fromDone = isNodeCompleted(conn.from);
  const toDone = isNodeCompleted(conn.to);
  if (fromDone && toDone) return 'rgba(184, 155, 94, 0.55)';
  if (fromDone || conn.to.status !== 'locked') return 'var(--color-aged-gold)';
  return 'rgba(61, 55, 50, 0.12)';
}

function isConnectionActive(conn: MapConnection): boolean {
  return isNodeCompleted(conn.from) || conn.to.status !== 'locked';
}

function getConnectionDash(conn: MapConnection): string {
  const active = isConnectionActive(conn);
  if (!active) return '4 4';
  if (conn.to.nodeType === 'bonus') return '6 4';
  return '0 1';
}

function getNodeTypeIcon(node: SkillNode): string {
  switch (node.nodeType) {
    case 'temple': return 'account_balance';
    case 'gate': return 'door_front';
    case 'gateway': return 'door_front';
    case 'boss': return 'stars';
    case 'bonus': return 'star';
    default: return 'account_balance';
  }
}

function getUnlockConditionText(node: SkillNode): string {
  if (!node.unlockCondition) return '';
  switch (node.unlockCondition.type) {
    case 'node_complete':
      return 'Completar nodo anterior';
    case 'xp_threshold':
      return `Alcanzar ${(node.unlockCondition.value ?? 0).toLocaleString()} AURA`;
    case 'cert_level':
      return `Certificacion nivel ${node.unlockCondition.level}`;
    default:
      return 'Desbloqueo manual';
  }
}

function onNodeClick(node: SkillNode) {
  if (node.status === 'locked') return;
  selectedNode.value = node;
  showNodeDialog.value = true;
}

async function completeNode(node: SkillNode) {
  completing.value = true;
  try {
    const { data } = await api.post('/certification/node/complete', {
      nodeKey: node.key,
    });

    // Update node status locally
    const idx = nodes.value.findIndex((n) => n.id === node.id);
    if (idx >= 0) {
      nodes.value[idx] = { ...nodes.value[idx], status: 'completed' };
    }

    showNodeDialog.value = false;

    $q.notify({
      type: 'positive',
      message: `+${data.auraAwarded ?? node.xpReward} AURA`,
      caption: `Nodo "${node.label}" completado`,
      icon: 'emoji_events',
      timeout: 3000,
      position: 'top',
    });

    // Refresh tree to get updated statuses
    await loadSkillTree();
  } catch (err) {
    console.error('[ascenso] completeNode error:', err);
    $q.notify({
      type: 'negative',
      message: 'Error al completar el nodo',
      timeout: 3000,
    });
  } finally {
    completing.value = false;
  }
}

async function loadSkillTree() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await api.get('/certification/skill-tree');
    nodes.value = data.nodes ?? [];
    userXp.value = data.userXp ?? 0;

    // After loading, scroll to first available node
    await nextTick();
    scrollToFirstAvailable();
  } catch (err) {
    console.error('[ascenso] loadSkillTree error:', err);
    error.value = 'Error al cargar el arbol de habilidades';
  } finally {
    loading.value = false;
  }
}

function scrollToFirstAvailable() {
  const firstAvailable = layoutNodes.value.find(
    (n) => n.status === 'available' || n.status === 'in_progress',
  );
  if (!firstAvailable || !scrollContainer.value) return;

  const svgHeight = maxY.value + 60;
  const scrollableHeight = scrollContainer.value.scrollHeight;
  const targetRatio = firstAvailable.positionY / svgHeight;
  scrollContainer.value.scrollTo({
    top: Math.max(0, targetRatio * scrollableHeight - 200),
    behavior: 'smooth',
  });
}

onMounted(() => {
  loadSkillTree();
});
</script>

<style scoped>
.ascenso-page {
  padding: 16px;
}

.ascenso-container {
  max-width: 900px;
  margin: 0 auto;
}

.ascenso-loading {
  padding: 24px;
}

.ascenso-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 24px;
}

.empty-text {
  color: var(--oa-text-muted);
  font-size: 14px;
}

/* Header */
.ascenso-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.ascenso-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.ascenso-subtitle {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

.ascenso-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ascenso-progress-bar-container {
  width: 96px;
  height: 6px;
  border-radius: 3px;
  background: var(--oa-bg-3);
}

.ascenso-progress-bar {
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--oa-gold-dim), var(--oa-gold-full));
  transition: width 0.5s ease;
}

.ascenso-xp {
  font-size: 12px;
  font-family: monospace;
  font-weight: 700;
  color: var(--oa-gold-full);
}

/* Map wrapper */
.ascenso-map-wrapper {
  position: relative;
  background: var(--oa-bg-0);
  border-radius: 16px;
  border: 1px solid var(--oa-border);
}

/* Region sidebar */
.region-sidebar {
  position: absolute;
  left: 12px;
  top: 12px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 8px;
  padding: 6px 4px;
  background: color-mix(in srgb, var(--oa-bg-1) 90%, transparent);
  backdrop-filter: blur(8px);
  border: 1px solid var(--oa-border);
}

.region-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;
}

.region-btn:hover:not(:disabled) {
  background: var(--oa-bg-2);
}

.region-btn:disabled {
  cursor: default;
}

.region-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.region-label {
  font-size: 9px;
  font-weight: 500;
}

/* Scroll area */
.ascenso-scroll-area {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 72vh;
  padding: 24px 40px 16px;
}

.ascenso-svg {
  width: 100%;
  height: auto;
  min-height: 400px;
}

/* Scroll fade */
.ascenso-scroll-fade {
  pointer-events: none;
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  height: 48px;
  background: linear-gradient(transparent, var(--oa-bg-0));
  border-radius: 0 0 16px 16px;
}

/* Legend */
.ascenso-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 12px 16px;
  border-top: 1px solid var(--oa-border);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: var(--oa-text-muted);
}

.legend-dot {
  width: 8px;
  height: 8px;
}

/* Node clickable */
.node-clickable {
  cursor: pointer;
}

/* Node detail dialog */
.node-detail-card {
  width: 400px;
  max-width: 90vw;
  background: var(--oa-bg-1) !important;
  border-left: 1px solid var(--oa-border);
}

.node-detail-header {
  background: linear-gradient(135deg, var(--oa-bg-2), var(--oa-bg-1));
  border-bottom: 1px solid var(--oa-border);
  padding: 20px 24px;
}

.node-detail-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.node-detail-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.node-detail-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.node-detail-type {
  font-size: 12px;
  color: var(--oa-text-muted);
  text-transform: capitalize;
  margin: 0;
}

.node-detail-body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.node-detail-desc {
  font-size: 14px;
  color: var(--oa-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.node-detail-badges {
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.badge-completed,
.badge-mastered {
  background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  color: var(--oa-gold-full);
  border: 1px solid var(--oa-gold-dim);
}

.badge-available,
.badge-in_progress {
  background: var(--oa-bg-2);
  color: var(--oa-text-primary);
  border: 1px solid var(--oa-border);
}

.badge-locked {
  background: var(--oa-bg-2);
  color: var(--oa-text-disabled);
  border: 1px solid var(--oa-border);
}

.badge-xp {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent);
  color: var(--oa-gold-full);
  border: 1px solid var(--oa-gold-dim);
}

.node-progress-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.node-progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--oa-text-muted);
}

.node-detail-section {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 8px;
  padding: 12px;
}

.node-section-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--oa-text-muted);
  margin: 0 0 4px 0;
}

.node-section-text {
  font-size: 14px;
  color: var(--oa-text-primary);
  margin: 0;
}

.node-hero-section {
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent);
  border: 1px solid var(--oa-gold-dim);
  border-radius: 8px;
  padding: 12px;
  text-align: center;
}

.node-hero-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--oa-text-muted);
  margin: 0;
}

.node-hero-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--oa-gold-full);
  margin: 4px 0 0 0;
}

.node-hero-epithet {
  font-size: 12px;
  font-style: italic;
  color: var(--oa-gold-mid);
  margin: 4px 0 0 0;
}

.node-phrase-section {
  border-left: 2px solid var(--oa-gold-dim);
  padding-left: 12px;
}

.node-phrase-text {
  font-size: 14px;
  font-style: italic;
  color: var(--oa-gold-mid);
  margin: 0;
  line-height: 1.5;
}

.node-region-info {
  font-size: 11px;
  color: var(--oa-text-disabled);
  margin: 0;
}

.node-tip-text {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}

.node-complete-btn {
  background: var(--oa-gold-full) !important;
  color: var(--color-marble-cream) !important;
  font-weight: 600;
  border-radius: 8px;
  margin-top: 8px;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .ascenso-page {
    padding: 8px;
  }

  .ascenso-scroll-area {
    padding: 16px 12px 8px;
    max-height: 65vh;
  }

  .region-sidebar {
    display: none;
  }

  .ascenso-legend {
    flex-wrap: wrap;
    gap: 12px;
  }

  .node-detail-card {
    width: 100%;
  }
}
</style>
